<template>
  <div class="leaderboards">
    <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

    <div class="columns" style="margin-bottom: 0;">
      <div class="column">
        <h2 class="title is-3 has-text-left" style="color: gray">
          Leaderboards
        </h2>
      </div>
      <div class="column">
        <!-- Select Limit -->
        <div
          class="is-flex is-justify-content-center is-align-items-center mb-4"
        >
          <h1 class="is-size-3">Top &nbsp;</h1>
          <b-select v-model="limit" size="is-large">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </b-select>
        </div>
      </div>
      <div class="column has-text-right">
        <router-link to="/ratings">
          <a class="is-size-4 has-text-link">
            Ratings &gt;&gt;
          </a>
        </router-link>
      </div>
    </div>

    <div class="leaderboards__options">
      <!-- Select Type|Space -->
      <section>
        <b-field>
          <b-radio-button v-model="typeSpace" native-value="Students|Country">
            <span>ALL PLAYERS</span>
          </b-radio-button>

          <b-radio-button v-model="typeSpace" native-value="Schools|Country">
            <span>ALL SCHOOLS</span>
          </b-radio-button>

          <b-radio-button v-model="typeSpace" native-value="Students|School">
            <span>MY SCHOOL</span>
          </b-radio-button>
        </b-field>
      </section>

      <!-- Select Period -->
      <section>
        <b-field>
          <b-radio-button v-model="periodType" native-value="Weekly">
            <span>THIS WEEK</span>
          </b-radio-button>

          <b-radio-button v-model="periodType" native-value="Quarterly">
            <span>THIS QUARTER</span>
          </b-radio-button>

          <b-radio-button v-model="periodType" native-value="AllTime">
            <span>ALL TIME</span>
          </b-radio-button>
        </b-field>
      </section>
    </div>

    <div class="leaderboards__table">
      <!-- Students Leaderboard -->
      <table
        class="table is-striped is-fullwidth is-narrow"
        v-show="type === 'Students'"
      >
        <tbody v-show="ranks">
          <tr v-for="(item, i) in ranks" :key="item.user_id">
            <td>
              <span
                class="title is-5 has-text-white"
                style="white-space:nowrap;"
              >
                {{ i + 1 }}
              </span>
            </td>

            <td style="width: 450px;">
              <ProfileCard
                :player="item"
                type="long"
                color="white"
                :onlinePlayer="onlinePlayer(item)"
                :challenge="true"
              />
            </td>

            <td style="text-align: left">
              <PlayerRating
                :rating="playerRating(item.username)"
                :diff="playerRatingDiff(item.username)"
                type="large"
              />
            </td>
            <td>
              <MasteryLevel :level="playerLevel(item.username)" />
            </td>
            <td>
              <PlayerWinStats :stats="playerStats(item.username)" />
            </td>
            <td class="has-text-right is-size-5">
              <b> {{ Number(item.score).toLocaleString() }}</b>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Schools Leaderboard -->
      <table class="table is-striped is-fullwidth" v-show="type === 'Schools'">
        <tbody v-show="ranks">
          <tr v-for="(item, i) in ranks" :key="'school' + i">
            <td>
              <span
                class="title is-5 has-text-white"
                style="white-space:nowrap;"
              >
                {{ i + 1 }}
              </span>
            </td>
            <td class="has-text-left is-size-5">
              {{ item.school ? item.school.school_name : "" }}
            </td>
            <td class="has-text-right is-size-5">
              <b> {{ Number(item.score).toLocaleString() }}</b>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import ProfileCard from "@/components/players/ProfileCard.vue";
import MasteryLevel from "@/components/stats/MasteryLevel.vue";
import PlayerRating from "@/components/stats/PlayerRating.vue";
import PlayerWinStats from "@/components/stats/PlayerWinStats.vue";
import { LeaderboardsAPI } from "@/services/api";
import { MatchesAPI } from "@/services/api";
import { mapGetters } from "vuex";

export default {
  components: { ProfileCard, MasteryLevel, PlayerRating, PlayerWinStats },
  name: "Leaderboards",
  title() {
    return "Leaderboards";
  },

  data() {
    return {
      typeSpace: "Students|Country",
      periodType: "Weekly",
      limit: 10,
      isLoading: false,
      leaderboards: {},
      ranks: null,
      ratings: null,
    };
  },

  computed: {
    ...mapGetters(["getProfile", "getOnlinePlayers"]),

    type() {
      return this.typeSpace.split("|")[0];
    },

    space() {
      return this.typeSpace.split("|")[1];
    },

    spaceCode() {
      if (this.space === "School") return this.getProfile.school.school_code;
      return null;
    },

    key() {
      return (
        this.type + this.space + this.spaceCode + this.periodType + this.limit
      );
    },
  },

  watch: {
    key: {
      immediate: true,
      handler() {
        this.queryLeaderboard();
      },
    },
  },

  methods: {
    async queryLeaderboard() {
      this.ranks = null;
      if (!this.key) return;

      let leaderboard = this.leaderboards[this.key];
      if (!leaderboard) {
        this.isLoading = true;
        leaderboard = await LeaderboardsAPI.get({
          Type: this.type,
          Space: this.space,
          SpaceCode: this.spaceCode,
          PeriodTYpe: this.periodType,
          Limit: this.limit,
        });

        if (
          this.type === "Students" &&
          leaderboard.ranks &&
          leaderboard.ranks.length
        ) {
          const usernames = leaderboard.ranks.map((item) => item.username);
          leaderboard.ratings = await MatchesAPI.getPlayersStats(usernames);
        }
        this.leaderboards[this.key] = leaderboard;
      }

      this.ranks = leaderboard.ranks;
      this.ratings = leaderboard.ratings;
      this.isLoading = false;
    },

    onlinePlayer(player) {
      if (!player || !player.user_id) return null;
      if (!this.getOnlinePlayers || !this.getOnlinePlayers.length) return null;
      return this.getOnlinePlayers.find((p) => p.user_id === player.user_id);
    },

    playerStats(username) {
      if (!this.ratings || !this.ratings.length) return null;
      return this.ratings.find((r) => r.player_id === username);
    },

    playerLevel(username) {
      if (!this.playerStats(username)) return 0;
      return this.playerStats(username).level;
    },

    playerRating(username) {
      if (!this.playerStats(username)) return null;
      return this.playerStats(username).rating;
    },

    playerRatingDiff(username) {
      if (!this.playerStats(username)) return null;
      return this.playerStats(username).rating_diff;
    },
  },
};
</script>

<style scoped>
.leaderboards {
  position: relative;
}

.leaderboards__options {
  display: flex;
  justify-content: space-between;
  border-bottom: 2px solid grey;
}

tr {
  background-color: #674ea7;
  color: white;
}

.table.is-striped tbody tr:not(.is-selected):nth-child(even) {
  background-color: #b400b4;
  color: white;
}

td {
  vertical-align: middle !important;
}
</style>
